import { ClinicMark } from '@/components/ClinicMark'
import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { ApiError } from '@/api/errors'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import { PasswordField } from '@/components/PasswordField'
import { TextField } from '@/components/TextField'
import { useSession } from './SessionProvider'
import { errorMessage } from '@/api/messages'

/** Each line is true of this codebase and checkable — the append-only audit
 *  trigger, the IndexedDB draft, the FORCEd row-level security policy. */
const ASSURANCES: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'shield',
    title: 'Every access is recorded',
    body: 'Reading, writing and exporting a record all append to a log the application itself cannot edit.',
  },
  {
    icon: 'save',
    title: 'A consultation is never lost',
    body: 'What you type is held on the device and only cleared once the server confirms the save.',
  },
  {
    icon: 'lock',
    title: 'One clinic cannot see another',
    body: 'Isolation is enforced by the database on every query, not by a filter someone has to remember.',
  },
]

export function SignInScreen() {
  const { signIn } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [capsLock, setCapsLock] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await signIn({ email, password })
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(errorMessage(caught))
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  // Warned about, never blocked: a password may legitimately be all capitals.
  function trackCapsLock(event: KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState?.('CapsLock') ?? false)
  }

  return (
    <div className="grid min-h-dvh grid-cols-1 bg-surface lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      {/* Desktop only: on a phone this space belongs to the form. */}
      <aside className="hidden flex-col justify-between gap-12 bg-brand px-10 py-12 text-brand-fg lg:flex">
        <div className="flex items-center gap-3">
          <ClinicMark className="size-11" />
          <span>
            <span className="block text-lg font-bold tracking-snug">Casemark</span>
            <span className="block text-xs text-brand-muted">Dr. Warrier&apos;s Homoeo Clinic</span>
          </span>
        </div>

        <div>
          {/* `text-balance`, not the `pretty` every other paragraph inherits:
              at this size and width the break is part of the composition, and
              balanced it falls at the comma — "The OP sheet, / kept properly."
              rather than dropping "properly." onto a line of its own. */}
          <p className="mb-8 max-w-[16ch] text-3xl leading-tight font-bold tracking-tighter text-balance">
            The OP sheet, kept properly.
          </p>
          <ul className="m-0 grid max-w-[34rem] list-none gap-5 p-0 [&>li]:flex [&>li]:items-start [&>li]:gap-3 [&_strong]:block [&_strong]:text-sm [&_strong]:font-semibold">
            {ASSURANCES.map((item) => (
              <li key={item.title}>
                <span className="grid size-8 flex-none place-items-center rounded-md border border-brand-line text-brand-fg">
                  <Icon name={item.icon} size="1.125rem" />
                </span>
                <span>
                  <strong>{item.title}</strong>
                  <span className="block text-sm leading-normal text-brand-muted">{item.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="max-w-[44ch] border-t border-brand-line pt-5 text-xs text-brand-muted">
          Authorised clinical staff only. Access to patient records is logged and audited.
        </p>
      </aside>

      <main className="flex items-center justify-center p-6">
        <form className="flex w-full max-w-96 flex-col gap-4" onSubmit={onSubmit}>
          {/* The lockup the brand panel carries on a desktop; the form needs its
              own on a phone, where that panel is not rendered. */}
          <div className="mb-4 flex items-center gap-3 lg:hidden">
            <ClinicMark className="size-11" />
            <span>
              <span className="block text-lg font-bold tracking-snug">Casemark</span>
              <span className="block text-xs text-fg-muted">Dr. Warrier&apos;s Homoeo Clinic</span>
            </span>
          </div>

          <div className="grid gap-1">
            <h1 className="text-3xl leading-tight font-bold tracking-tighter">Sign in</h1>
            <p className="text-sm text-fg-muted">Use the account your clinic issued you.</p>
          </div>

          {error && (
            <Banner tone="danger" live>
              {error}
            </Banner>
          )}

          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="username"
            placeholder="you@clinic.example"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />

          <PasswordField
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyUp={trackCapsLock}
            onBlur={() => setCapsLock(false)}
            required
            {...(capsLock ? { hint: 'Caps Lock is on.' } : {})}
          />

          <Button type="submit" size="large" block loading={busy}>
            Sign in
          </Button>

          <p className="pt-2 text-center text-xs text-fg-subtle">
            Trouble signing in? Ask your clinic administrator to reset your password.
          </p>
        </form>
      </main>
    </div>
  )
}
